<template>
    <div>
        <box>
            <template v-slot:header>
                <h4>
                    <i class="fas fa-list"></i> {{resource('JourneyComposer_Overview_Header')}}
                </h4>
            </template>

            <template v-slot:body>
                <div class="row mb-3">
                    <div class="col-6">
                        <a :href="journeyStatisticsUrl">
                            <button class="btn btn-outline-secondary pull-left">
                                <i class="fas fa-chart-area"></i> {{resource('JourneyComposer_Overview_JourneyReport_Button')}}
                            </button>
                        </a>
                    </div>
                    <div class="col-6">
                        <button class="btn btn-primary pull-right" @click="newJourney" v-if="hasWritePermission">
                            <i class="fas fa-plus"></i> {{resource('JourneyComposer_Overview_NewJourney_Button')}}
                        </button>
                    </div>
                </div>

                <!-- Using a key causes a full reload upon key change, this is required when adding new columns -->
                <data-table class="table table-striped" :key="columns.length"
                    ref="table"
                    :data="mappedJourneys"
                    :columns="columns"

                    @edit="handleEdit($event.id)"
                    @navigateToStatistics="navigateToStatistics($event.id)"
                    @delete="prepareDelete($event.id)"
                    @clone="prepareClone($event.id)"
                />

                <prompt v-if="showPrompt"
                    :title="promptTitle"
                    :body="promptBody"
                    :size="promptsize"
                    icon="far fa-trash-alt text-danger"

                    @yes="promptHandler"
                    @hidden="showPrompt = false"
                />

            </template>
        </box>
        
        <box>
            <template v-slot:body>
                <advanced-sankey-diagram />
            </template>
        </box>
    </div>
</template>

<script>

//Components
import BootstrapBox from '../../../../Components/Util/BootstrapBox.vue';
import DataTable, { defaultContentType } from '../../../../Components/Util/DataTable/DataTable.vue';
import Prompt, {sizes} from '../../../../Components/Util/Prompt.vue';
import AdvancedSankeyDiagram from "../Charts/AdvancedSankeyDiagram.vue";

//Mixins
import resourceMixin from '../../../../Util/Mixins/resources';
import tooltipsMixin from '../../../../Util/Mixins/tooltips';
import cloneJourneyMixin from '../../Mixins/clone-journey'
import permissionsMixin from '../../Mixins/journey-permissions'

//Util
import api, {controllerEndpoint} from '../../api'
import { bootstrapSuccessGrowl, bootstrapDangerGrowl } from '../../../../Util/growler';
import divisionsApi from "../../../../Api/divisions";

export default {
    name: 'journey-builder-overview',
    mixins: [resourceMixin, cloneJourneyMixin, permissionsMixin, tooltipsMixin],
    components: {
        'box': BootstrapBox,
        DataTable,
        Prompt,
        AdvancedSankeyDiagram
    },
    created() {
        this.currentJourneys = this.journeys;

        const divisionsPromise = divisionsApi.getDivisionsList({ includeDisabled: true }).then(
            divisionList => this.divisionList = divisionList,
            bootstrapDangerGrowl
        );

        const worldsPromise = api.getWorlds().then(
            (worlds) => {
                this.worlds = worlds;
            },
            bootstrapDangerGrowl
        );
        
        //Our table has probably already had a render cycle prior to our worlds/divisions being available
        //trigger a re-render post load to ensure names are properly displayed
        Promise.all([divisionsPromise, worldsPromise]).finally(() => this.reloadTable());
    },
    methods: {
        reloadTable(){
            const table = this.$refs.table;
            if(table){
                table.reloadDataTable();
            }
        },
        handleEdit(id) {
            window.location.href = `${controllerEndpoint}Index/${id}`;
        },
        prepareDelete(id) {
            if(this.hasDeletePermission) {
                this.promptTitle = this.resource('JourneyBuilder_Overview_Delete_Prompt_Title');
                this.promptBody = this.resource('JourneyBuilder_Overview_Delete_Prompt_Body');
                
                this.promptHandler = () => {
                    this.handleDelete(id);
                }

                this.showPrompt = true;
            }
        },
        prepareClone(id) {
            if(this.canWrite) {
                this.promptTitle = this.resource('JourneyBuilder_Overview_Clone_Prompt_Title');
                this.promptBody = this.resource('JourneyBuilder_Overview_Clone_Prompt_Body');
                
                this.promptHandler = () => {
                    this.cloneJourney(id);
                }
                
                this.showPrompt = true;
            }
        },
        handleDelete(id) {
            const vm = this;

            if(id && vm.hasDeletePermission) {
                api.deleteJourneyById(id).then(
                    () => {
                        vm.currentJourneys = vm.currentJourneys.filter((journey) =>
                            journey.id !== id
                        );

                        this.reloadTable();
                        //vm.$refs.table.reloadDataTable();
                        bootstrapSuccessGrowl(this.resource('JourneyBuilder_Overview_Delete_SuccessMessage'));
                    },
                    bootstrapDangerGrowl
                )
            }
        },
        navigateToStatistics(journeyId) {
            window.location.href = api.getStatisticsPageUrl(journeyId);
        },
        newJourney() {
            window.location.href = `${controllerEndpoint}Index`;
        },
        createButton(outline, text, icon, tooltip) {
            const iconHtml = icon ? `<i class="${icon}"></i>` : '';
            const tooltipHtml = tooltip ? `data-toggle="tooltip" data-placement="top" title="${tooltip}"` : '';

            return `
                <button class="btn ${outline} btm-sm" ${tooltipHtml}>
                ${iconHtml}
                </button>`;
        }
    },
    data() {
        return {
            promptTitle: '',
            promptBody: '',
            currentJourneys: [],
            worlds: [],

            /**
             * @type {DivisionTree}
             */
            divisionList: undefined,
            showPrompt: false,
            journeyStatisticsUrl: api.getStatisticsPageUrl(),
            promptHandler: () => {}
        }
    },
    computed: {
        promptsize() {
            return sizes.medium;
        },
        divisionsNameMap(){
            const divisionsNameMap = new Map();

            let unprocessedDivisions = [];
            let currentDivision = this.divisionList;

            while(currentDivision){
                //It is possible for a division to have two parents
                //we only care about the first encounter
                if(divisionsNameMap.has(currentDivision.id)){
                    continue;
                }

                //Register division name
                divisionsNameMap.set(currentDivision.id, currentDivision.name);

                currentDivision.childDivisions.forEach(childDivision => {
                    if(!divisionsNameMap.has(childDivision.id)){
                        unprocessedDivisions.push(childDivision);
                    }
                });

                //Popping an empty array returns undefined
                //therefor we should just have to check for truthyness of our currentDivision
                currentDivision = unprocessedDivisions.pop();
            }
            
            return divisionsNameMap;
        },
        columns(){
            const editBtn = this.createButton(
                'btn-outline-secondary',
                this.hasWritePermission ? this.resource('Button_Edit') : this.resource('Button_View'),
                this.hasWritePermission ? 'fas fa-edit' : 'fas fa-eye',
                this.resource('JourneyBuilder_Overview_Edit_Button_Tooltip')
            );

            const deleteBtn = this.createButton(
                'btn-outline-secondary',
                this.resource('Button_Remove'),
                'far fa-trash-alt',
                this.resource('JourneyBuilder_Overview_Delete_Button_Tooltip')
            );
            
            const statisticsBtn = this.createButton(
                'btn-outline-secondary',
                this.resource('JourneyBuilder_Shared_StatisticsButton_DisplayText'),
                'fas fa-project-diagram',
                this.resource('JourneyBuilder_Overview_Report_Button_Tooltip')
            );
            
            const cloneBtn = this.createButton(
                'btn-outline-secondary',
                this.resource('JourneyBuilder_Shared_CloneButton_DisplayText'),
                'fas fa-clone',
                this.resource('JourneyBuilder_Overview_Clone_Button_Tooltip')
            );

            const columns = [
                { data: 'id', visible: false},
                { title: this.resource('JourneyModel_Name_DisplayName'), data: 'name'},
                { title: this.resource('JourneyModel_World_DisplayName'), data: 'world'},
                { title: this.resource('JourneyBuilderPublishModel_DivisionId_DisplayName'), data: 'division'},
                { title: this.resource('JourneyModel_Description_DisplayName'), data: 'description'},
                { title: this.resource('JourneyModel_Version_DisplayName'), data: 'version'}
            ];

            columns.push({ 
                title: this.resource('JourneyModel_IsLocked_DisplayName'),
                data: 'isLocked',
                render: function ( data, type, row, meta ) {
                    return data ? '<i class="fas fa-lock display-icon"></i>' : '<i class="fas fa-lock-open display-icon"></i>';
                }
            });

            columns.push({ 
                title: this.resource('JourneyModel_IsPublished_DisplayName'),
                data: 'isPublished',
                render: function ( data, type, row, meta ) {
                    return data ? '<i class="fas fa-check display-icon"></i>' : '-';
                }
            });

            if(this.hasWritePermission){
                columns.push({ 
                    name: 'clone',
                    data: null,
                    defaultContent: cloneBtn,
                    type: defaultContentType.button,
                    width: '30px',
                    sClass: 'icon-button-padding',
                    orderable: false});
            }

            columns.push({ 
                name: 'edit', 
                data: null, 
                defaultContent: editBtn, 
                type: defaultContentType.button, 
                width: '30px', 
                sClass: 'icon-button-padding', 
                orderable: false
            });
            
            if(this.hasDeletePermission){
                columns.push({
                    name: 'delete',
                    data: null,
                    defaultContent: deleteBtn,
                    type: defaultContentType.button,
                    width: '30px',
                    sClass: 'icon-button-padding',
                    orderable: false
                });
            }
            
            columns.push({ 
                name: 'navigateToStatistics', 
                data: null, 
                defaultContent: statisticsBtn, 
                type: defaultContentType.button, 
                width: '30px', 
                sClass: 'icon-button-padding',
                orderable: false,
            });

            return columns;
        },
        mappedJourneys() {
            const vm = this;
            
            return vm.currentJourneys.map((journey) => {

                const world = vm.worlds.find((world) => world.id === journey.worldId);
                const worldName = world ? world.name : '-';

                return {
                    id: journey.id,
                    name: journey.name,
                    
                    //On initial load our map might not be filled yet
                    //display '-' as a placeholder
                    division: this.divisionsNameMap.get(journey.divisionId) || '-',
                    world:  worldName,
                    description: journey.description,
                    isLocked: journey.isLocked,
                    isPublished: journey.isPublished,
                    version: journey.version
                }
            });
        }
    },
    props: {
        journeys: {
            type: Array,
            required: true
        }
    }
};
</script>

<style lang="scss" scoped>

.btn.btn-primary.pull-right {
    margin-right: 15px;
}

</style>